// components/ProspectDownload.jsx - New component for handling download links

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, ProgressBar } from 'react-bootstrap';
import { useTheme } from '../ThemeContext';
import { Check, X, FileText, Download } from 'react-feather';
import { apifetch } from '../services/interceptors';
import generateSecurityPDF from './pdfGenerator';
import ResultsPage from './PipsqueakWidget';

const ProspectDownload = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Fetching report data...');
  const [downloadComplete, setDownloadComplete] = useState(false);
  // Set only when this scan is a slide-deck report; the page then renders the deck
  // instead of triggering a file download.
  const [deckData, setDeckData] = useState(null);

  useEffect(() => {
    const downloadReport = async () => {
      try {
        if (!scanId || scanId === 'temp') {
          setError('Invalid download link. Please contact the sender.');
          setLoading(false);
          return;
        }

        setStatus('Fetching report data...');
        setProgress(20);

        // Fetch the scan data from the backend
        const response = await apifetch(`/s2/api/download/report/${scanId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Report not found or expired');
        }

        const data = await response.json();
        const { results, domain } = data;

        if (!results || !domain) {
          throw new Error('Invalid report data');
        }

        // The salesperson picked a style for this prospect when they ran the scan; the
        // server has already resolved it against the tenant default. A slide deck is a
        // page to look at, not a file, so render it instead of downloading anything.
        if (data.report_style === 'slide_deck') {
          setDeckData({ results, domain });
          setLoading(false);
          return;
        }

        setStatus('Generating PDF...');
        setProgress(50);

        // Generate PDF using the existing utility
        await generateSecurityPDF(results, domain, {
          theme: 'light',
          prospectName: '',
          prospectCompany: '',
          isCyberMode: false,
          saveFile: true,
          fileName: `security-report-${domain}-${Date.now()}.pdf`,
          onProgress: (step, message) => {
            const progressValue = 50 + (step * 0.4);
            setProgress(Math.min(progressValue, 90));
            setStatus(message || `Generating PDF... ${step}%`);
          }
        });

        setProgress(100);
        setStatus('Download complete!');
        setDownloadComplete(true);

        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);

      } catch (error) {
        console.error('Download error:', error);
        setError(error.message || 'Failed to download report');
        setLoading(false);
      }
    };

    downloadReport();
  }, [scanId, navigate]);

  // Slide-deck reports render as a presentation the prospect can page through, rather
  // than downloading a file. `gated={false}` because the salesperson already chose to
  // share this — the prospect must not be asked for their details again to see it.
  // Uses the same forcedStyle override InternalReportPreview relies on.
  if (deckData) {
    return (
      <ResultsPage
        embeddedResults={deckData.results}
        embeddedTheme={isDarkMode ? 'dark' : 'light'}
        forcedStyle="slide_deck"
        gated={false}
      />
    );
  }

  // If there's an error, show error state
  if (error) {
    return (
      <Container className="py-5">
        <Card className={`border-0 shadow-lg ${isDarkMode ? 'bg-dark text-light' : 'bg-white'}`}>
          <Card.Body className="text-center py-5">
            <div className="display-1 text-danger mb-4">
              <X size={64} />
            </div>
            <h4 className="mb-3">Download Failed</h4>
            <p className="text-muted">{error}</p>
            <Button 
              variant="primary" 
              onClick={() => navigate('/')}
              className="mt-3"
            >
              Go to Home
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Show success state
  if (downloadComplete) {
    return (
      <Container className="py-5">
        <Card className={`border-0 shadow-lg ${isDarkMode ? 'bg-dark text-light' : 'bg-white'}`}>
          <Card.Body className="text-center py-5">
            <div className="display-1 text-success mb-4">
              <Check size={64} />
            </div>
            <h4 className="mb-3">Download Complete!</h4>
            <p className="text-muted">Your security report has been downloaded successfully.</p>
            <p className="text-muted small">Redirecting to home...</p>
            <Button 
              variant="primary" 
              onClick={() => navigate('/')}
              className="mt-3"
            >
              Go to Home Now
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Show loading state
  return (
    <Container className="py-5">
      <Card className={`border-0 shadow-lg ${isDarkMode ? 'bg-dark text-light' : 'bg-white'}`}>
        <Card.Body className="text-center py-5">
          <div className="mb-4">
            <FileText size={48} className="text-primary" />
          </div>
          <h4 className="mb-3">Preparing Your Report</h4>
          <p className="text-muted mb-4">{status}</p>
          
          <div className="mx-auto" style={{ maxWidth: '400px' }}>
            <ProgressBar 
              now={progress} 
              label={`${Math.round(progress)}%`}
              variant={progress === 100 ? 'success' : 'primary'}
              className="mb-3"
              style={{ height: '20px' }}
            />
          </div>
          
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Spinner animation="border" variant="primary" size="sm" />
            <small className="text-muted">Please wait while we generate your report...</small>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ProspectDownload;